class DownloadButton extends HTMLElement {
  #abortController;
  #href;
  #fileName;
  #files;
  #zipGroups;
  #defaultContent;

  constructor() {
    super();
  }

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.addEventListener('click', async () => {
      if (this.dataset.downloading === 'true') {
        return;
      }

      if (this.#zipGroups?.length) {
        try {
          this.#setDownloading(true);
          await this.#downloadZipGroups();
          this.#setStatus('Download Ready');
          setTimeout(() => this.#resetStatus(), 1600);
        } catch {
          this.#setStatus('Download Failed');
          setTimeout(() => this.#resetStatus(), 2200);
        } finally {
          this.#setDownloading(false);
        }
        return;
      }

      const files = this.#files || [];
      if (files.length) {
        files.forEach((obj) => {
          this.#onDownload(obj.url, obj.name);
        });
      } else if (this.#href) {
        this.#onDownload(this.#href, this.#fileName || 'file');
      }
    }, { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = null;
  }

  static get observedAttributes() {
    return ['href', 'file-name', 'files', 'zip-groups'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'href' && newValue) {
      this.#href = newValue;
    }
    if (name === 'file-name' && newValue) {
      this.#fileName = newValue;
    }
    if (name === 'files' && newValue) {
      this.#files = JSON.parse(newValue);
    }
    if (name === 'zip-groups') {
      this.#zipGroups = newValue ? JSON.parse(newValue) : null;
    }
  }

  async #downloadZipGroups() {
    for (const group of this.#zipGroups) {
      const groupFiles = group.files || [];
      const files = await this.#fetchGroupFiles(groupFiles);

      if (!files.length) {
        continue;
      }

      this.#setStatus('Zipping Files');
      const blob = await this.#buildZip(files);
      this.#triggerBlobDownload(blob, group.fileName || 'downloads.zip');
    }
  }

  async #fetchGroupFiles(files) {
    const fetchedFiles = [];

    for (const [index, file] of files.entries()) {
      try {
        this.#setStatus('Preparing');
        const response = await fetch(file.url);
        if (!response.ok) {
          continue;
        }
        const blob = await response.blob();
        fetchedFiles.push({
          name: file.name,
          blob,
        });
      } catch {
        // Skip individual files that fail to fetch and continue building the zip.
      }
    }

    return fetchedFiles;
  }

  #setDownloading(isDownloading) {
    this.dataset.downloading = isDownloading ? 'true' : 'false';
    this.toggleAttribute('aria-busy', isDownloading);
  }

  #setStatus(text) {
    if (!this.#defaultContent) {
      this.#defaultContent = this.innerHTML;
    }

    const textEl = this.querySelector('.elementor-button-text');
    if (textEl) {
      textEl.textContent = text;
      return;
    }

    this.textContent = text;
  }

  #resetStatus() {
    if (this.#defaultContent) {
      this.innerHTML = this.#defaultContent;
    }
  }

  async #buildZip(files) {
    const encoder = new TextEncoder();
    const localParts = [];
    const centralParts = [];
    let offset = 0;

    for (const file of files) {
      const fileName = this.#sanitizeFileName(file.name);
      const fileNameBytes = encoder.encode(fileName);
      const data = new Uint8Array(await file.blob.arrayBuffer());
      const crc32 = this.#crc32(data);
      const localHeader = this.#buildLocalHeader(fileNameBytes, data.length, crc32);
      localParts.push(localHeader, fileNameBytes, data);
      centralParts.push(this.#buildCentralHeader(fileNameBytes, data.length, crc32, offset));
      offset += localHeader.length + fileNameBytes.length + data.length;
    }

    const centralDirectorySize = centralParts.reduce((total, part) => total + part.length, 0);
    const endRecord = this.#buildEndRecord(centralParts.length, centralDirectorySize, offset);

    return new Blob([...localParts, ...centralParts, endRecord], { type: 'application/zip' });
  }

  #sanitizeFileName(fileName) {
    return fileName.replace(/[\\/:*?"<>|]/g, '-');
  }

  #buildLocalHeader(fileNameBytes, size, crc32) {
    const view = new DataView(new ArrayBuffer(30));
    view.setUint32(0, 0x04034b50, true);
    view.setUint16(4, 20, true);
    view.setUint16(6, 0, true);
    view.setUint16(8, 0, true);
    view.setUint16(10, 0, true);
    view.setUint16(12, 0, true);
    view.setUint32(14, crc32, true);
    view.setUint32(18, size, true);
    view.setUint32(22, size, true);
    view.setUint16(26, fileNameBytes.length, true);
    view.setUint16(28, 0, true);
    return new Uint8Array(view.buffer);
  }

  #buildCentralHeader(fileNameBytes, size, crc32, offset) {
    const view = new DataView(new ArrayBuffer(46));
    view.setUint32(0, 0x02014b50, true);
    view.setUint16(4, 20, true);
    view.setUint16(6, 20, true);
    view.setUint16(8, 0, true);
    view.setUint16(10, 0, true);
    view.setUint16(12, 0, true);
    view.setUint16(14, 0, true);
    view.setUint32(16, crc32, true);
    view.setUint32(20, size, true);
    view.setUint32(24, size, true);
    view.setUint16(28, fileNameBytes.length, true);
    view.setUint16(30, 0, true);
    view.setUint16(32, 0, true);
    view.setUint16(34, 0, true);
    view.setUint16(36, 0, true);
    view.setUint32(38, 0, true);
    view.setUint32(42, offset, true);
    return this.#concatUint8Arrays(new Uint8Array(view.buffer), fileNameBytes);
  }

  #buildEndRecord(entryCount, centralDirectorySize, centralDirectoryOffset) {
    const view = new DataView(new ArrayBuffer(22));
    view.setUint32(0, 0x06054b50, true);
    view.setUint16(4, 0, true);
    view.setUint16(6, 0, true);
    view.setUint16(8, entryCount, true);
    view.setUint16(10, entryCount, true);
    view.setUint32(12, centralDirectorySize, true);
    view.setUint32(16, centralDirectoryOffset, true);
    view.setUint16(20, 0, true);
    return new Uint8Array(view.buffer);
  }

  #concatUint8Arrays(...arrays) {
    const length = arrays.reduce((total, array) => total + array.length, 0);
    const merged = new Uint8Array(length);
    let offset = 0;

    arrays.forEach((array) => {
      merged.set(array, offset);
      offset += array.length;
    });

    return merged;
  }

  #crc32(data) {
    let crc = -1;

    for (let i = 0; i < data.length; i += 1) {
      crc = (crc >>> 8) ^ this.#crcTable[(crc ^ data[i]) & 0xff];
    }

    return (crc ^ -1) >>> 0;
  }

  get #crcTable() {
    if (!DownloadButton.crcTable) {
      const table = new Uint32Array(256);
      for (let i = 0; i < 256; i += 1) {
        let crc = i;
        for (let j = 0; j < 8; j += 1) {
          crc = (crc & 1) ? (0xedb88320 ^ (crc >>> 1)) : (crc >>> 1);
        }
        table[i] = crc >>> 0;
      }
      DownloadButton.crcTable = table;
    }

    return DownloadButton.crcTable;
  }

  #triggerBlobDownload(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const tag = document.createElement('a');
    tag.href = url;
    tag.download = filename;
    document.body.appendChild(tag);
    tag.click();
    window.URL.revokeObjectURL(url);
    tag.remove();
  }

  #onDownload(href, filename) {
    fetch(href)
      .then((resp) => {
        if (!resp.ok) {
          throw new Error(`Download failed: ${resp.status}`);
        }
        return resp.blob();
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const tag = document.createElement('a');
        tag.href = url;
        tag.download = filename;
        document.body.appendChild(tag);
        tag.click();
        window.URL.revokeObjectURL(url);
        tag.remove();
      })
      .catch(() => this.#altDownload(href, filename));
  }

  #altDownload(href = this.#href, filename = this.#fileName) {
    if (!href) {
      return;
    }

    const tag = document.createElement('a');
    tag.href = href;
    tag.target = '_blank';
    if (filename) {
      tag.download = filename;
    }
    document.body.appendChild(tag);
    tag.click();
    tag.remove();
  }
}

customElements.define('download-button', DownloadButton);
